import React, { useCallback, useState } from 'react';

import { Alert } from '@buildoutinc/blueprint-react/ui/Alert';
import { Tab, Tabs, TabsList } from '@buildoutinc/blueprint-react/ui/Tabs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { v4 as uuid } from 'uuid';

import { Button } from 'components/ui/button';
import { post } from 'helpers/api';
import { DATE_FORMAT, formatDate, ISO_DATE_FORMAT } from 'helpers/formatters/date_time_formats';

import CallPanel from './call_panel';
import EmailPanel from './email_panel';
import ActivityPanel from './activity_panel';
import { LOG_ACTIVITY_TABS, placeholders } from './config';
import { saveActivity } from '../form/helpers';
import { refreshActivities } from 'bundles/apex/activities/hooks';
import { buildRequestBody, errorMessage, splitEmails } from '../send_email/helpers';

import type { EmailFormValues } from './email_panel';
import type { Recipient } from './email_recipient';
import type { Activity } from 'bundles/apex/activities/types';
import type { LogActivityType } from './types';
import type { ApiError, EmailFile, SendEmailConfig } from '../send_email/types';
import { showSnackbar } from 'components/ui/snackbar/helper';

const EMPTY_EMAIL: EmailFormValues = { bcc: '', body: '', cc: '', subject: '', to: [] };
const GENERIC_ERROR = 'Something went wrong. Please try again.';

export interface LogActivityProps {
  path: string;
  subjectName?: string;
  fromEmail?: string;
  sendEmail?: SendEmailConfig;
  activity?: Activity;
  onLogged: (activity: Activity) => void;
  onClose?: () => void;
}

export default function LogActivity({
  path,
  subjectName,
  fromEmail,
  sendEmail,
  activity,
  onLogged,
  onClose,
}: LogActivityProps) {
  const editing = Boolean(activity);

  const [activeTab, setActiveTab] = useState<LogActivityType>(
    (activity?.activityType as LogActivityType) || 'note'
  );
  const [body, setBody] = useState(activity?.body ?? '');
  const [callResult, setCallResult] = useState('');
  const [emailFiles, setEmailFiles] = useState<EmailFile[]>([]);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState(() => uuid());
  const [errors, setErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const canSendEmail = Boolean(sendEmail?.canSend && sendEmail.path);

  const dateLabel = formatDate(activity?.date ?? new Date(), DATE_FORMAT);

  const buildEmail = useCallback((): EmailFormValues => ({
    ...EMPTY_EMAIL,
    to: (sendEmail?.to ?? []).map((email, index): Recipient => ({
      email,
      name: index === 0 ? subjectName : undefined,
      locked: true,
    })),
  }), [sendEmail, subjectName]);
  const [email, setEmail] = useState<EmailFormValues>(buildEmail);

  async function logActivity(activityType: LogActivityType, body: string) {
    if (!body.trim() || saving) return;

    setErrors([]);
    setSaving(true);

    try {
      const savedActivity: Activity = await saveActivity({
        activityType,
        body,
        date: activity?.date ?? formatDate(new Date(), ISO_DATE_FORMAT),
        path,
        ...(activity?.id ? { id: activity.id } : {}),
      });
      onLogged(savedActivity);
      showSnackbar({
        text: `Activity Successfully ${editing ? 'Updated' : 'Logged'}`,
        variant: 'success',
      });

      if (editing) {
        onClose?.();
      } else {
        setBody('');
        setEmail(buildEmail());
        setCallResult('');
      }
    } catch (error) {
      setErrors(Array.isArray(error) ? error : [GENERIC_ERROR]);
    } finally {
      setSaving(false);
    }
  }

  async function sendEmailActivity() {
    if (!sendEmail?.path || sendingEmail || email.to.length === 0) return;

    setErrors([]);
    setSendingEmail(true);

    try {
      const payload = {
        to: email.to.map(recipient => recipient.email),
        cc: splitEmails(email.cc),
        bcc: splitEmails(email.bcc),
        subject: email.subject,
        body: email.body,
      };
      await post(sendEmail.path, buildRequestBody(payload, emailFiles, idempotencyKey));

      refreshActivities();
      showSnackbar({ text: 'Email sent', variant: 'success', icon: faEnvelope });

      setEmail(buildEmail());
      setEmailFiles([]);
      setIdempotencyKey(uuid());
      onClose?.();
    } catch (error) {
      setErrors([errorMessage(error as ApiError)]);
    } finally {
      setSendingEmail(false);
    }
  }

  const renderActivityLogBody = () => {
    switch (activeTab) {
    case 'call':
      return (
        <CallPanel
          callResult={callResult}
          dateLabel={dateLabel}
          placeholder={placeholders.call(subjectName)}
          saving={saving}
          value={body}
          onCallResultChange={setCallResult}
          onChange={setBody}
          onSubmit={() => logActivity('call', body)}
        />
      );
    case 'message':
      return canSendEmail ? (
        <EmailPanel
          files={emailFiles}
          fromEmail={fromEmail}
          sending={sendingEmail}
          value={email}
          onAddFiles={files => setEmailFiles(prev => [...prev, ...files])}
          onChange={handleEmailChange}
          onRemoveFile={index => setEmailFiles(prev => prev.filter((_, i) => i !== index))}
          onSend={sendEmailActivity}
        />
      ) : (
        <ActivityPanel
          buttonLabel={editing ? 'Save Activity' : 'Log Email'}
          dateLabel={dateLabel}
          placeholder={placeholders.email(subjectName)}
          saving={saving}
          value={body}
          onChange={setBody}
          onSubmit={() => logActivity('message', body)}
        />
      );
    case 'note':
    case 'showing':
    case 'meeting':
      return (
        <ActivityPanel
          buttonLabel={editing ? 'Save Activity' : activeTab === 'note' ? 'Log Note' :
            `Log ${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}`
          }
          dateLabel={dateLabel}
          placeholder={placeholders.note(subjectName)}
          saving={saving}
          value={body}
          onChange={setBody}
          onSubmit={() => logActivity(activeTab, body)}
        />
      );
    }
  };

  function handleEmailChange(changes: Partial<EmailFormValues>) {
    setEmail(prev => ({ ...prev, ...changes }));
  }

  return (
    <div className="d-flex flex-column gap-3">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
        <h5 className="fw-bold m-0">{editing ? 'Edit Activity' : 'Log Activity'}</h5>
        <div className="d-flex align-items-center gap-2">
          <Tabs value={activeTab}>
            <TabsList variant="pills">
              {LOG_ACTIVITY_TABS.map(tab => (
                <Tab key={tab.key} value={tab.key} onClick={() => setActiveTab(tab.key)}>
                  <span className="d-flex align-items-center gap-1">
                    <FontAwesomeIcon icon={tab.icon} />
                    {tab.label}
                  </span>
                </Tab>
              ))}
            </TabsList>
          </Tabs>
          {onClose && <Button aria-label="Close" icon={faXmark} variant="ghost" onClick={onClose} />}
        </div>
      </div>

      {errors.length > 0 && (
        <Alert severity="destructive">
          {errors.map((error, i) => <div key={i}>{error}</div>)}
        </Alert>
      )}

      {renderActivityLogBody()}
    </div>
  );
}
